import { Route, Routes } from 'react-router';

import { RotaProtegida } from './components/rota-protegida';
import { AppShell } from './components/layout/app-shell';
import { PaginaLogin } from './pages/login';
import { PaginaEsqueciSenha } from './pages/esqueci-senha';
import { PaginaRedefinirSenha } from './pages/redefinir-senha';
import { PaginaTrocarSenha } from './pages/trocar-senha';
import { PaginaPainel } from './pages/painel';
import { PaginaSemPermissao } from './pages/sem-permissao';
import { PaginaListaSolicitacoes } from './pages/solicitacoes/lista';
import { PaginaNovaSolicitacao } from './pages/solicitacoes/nova';
import { PaginaDetalheSolicitacao } from './pages/solicitacoes/detalhe';
import { PaginaEditarSolicitacao } from './pages/solicitacoes/editar';
import { PaginaMarcacao } from './pages/solicitacoes/marcacao';
import { PaginaRelatorios } from './pages/relatorios';
import { PaginaHistoricoPaciente } from './pages/historico-paciente';
import { PaginaUsuarios } from './pages/admin/usuarios';
import { PaginaUbs } from './pages/admin/ubs';
import { PaginaEspecialidades } from './pages/admin/especialidades';
import { PaginaProcedimentos } from './pages/admin/procedimentos';
import { PaginaLocais } from './pages/admin/locais';
import { PaginaProfissionais } from './pages/admin/profissionais';
import { PaginaAuditoria } from './pages/admin/auditoria';
import { PaginaPec } from './pages/admin/pec';
import { PaginaEsus } from './pages/admin/esus';
import { PaginaSisreg } from './pages/admin/sisreg';
import { PaginaN8n } from './pages/admin/n8n';
import { PaginaFeriados } from './pages/admin/feriados';
import { PaginaSmtp } from './pages/admin/smtp';
import { PaginaModelosEmail } from './pages/admin/modelos-email';
import { PaginaRetencao } from './pages/admin/retencao';
import { PaginaZonaDePerigo } from './pages/admin/zona-de-perigo';
import { PaginaNaoEncontrada } from './pages/nao-encontrada';

/**
 * Rotas da aplicação.
 *
 * O guard do cliente é conveniência de navegação; a autorização real acontece
 * no backend, que revalida o perfil a cada requisição.
 */
export function App() {
  return (
    <Routes>
      {/* Públicas */}
      <Route path="/login" element={<PaginaLogin />} />
      <Route path="/esqueci-senha" element={<PaginaEsqueciSenha />} />
      <Route path="/redefinir-senha" element={<PaginaRedefinirSenha />} />

      {/* Troca de senha fica fora do shell: quando obrigatória, ocupa a tela
          inteira para não sugerir que há outro lugar para ir antes. */}
      <Route element={<RotaProtegida />}>
        <Route path="/trocar-senha" element={<PaginaTrocarSenha />} />
      </Route>

      {/* Autenticadas, dentro do layout */}
      <Route element={<RotaProtegida />}>
        <Route element={<AppShell />}>
          <Route path="/" element={<PaginaPainel />} />
          <Route path="/sem-permissao" element={<PaginaSemPermissao />} />

          {/* Solicitações: UBS e Regulação. O backend restringe a UBS à
              própria unidade e o cadastro apenas ao perfil UBS. */}
          <Route element={<RotaProtegida perfis={['UBS', 'REGULACAO', 'GESTOR']} />}>
            <Route path="/solicitacoes" element={<PaginaListaSolicitacoes />} />
          </Route>

          {/* O detalhe tem uma URL só, seja qual for a origem — e o
              Administrador entra nela por duas razões: ele visualiza a demanda
              reprimida, e é para cá que a tela de edição volta ao salvar, o que
              antes o deixava em "sem permissão" depois de corrigir. */}
          <Route
            element={<RotaProtegida perfis={['UBS', 'REGULACAO', 'GESTOR', 'ADMINISTRADOR']} />}
          >
            <Route path="/solicitacoes/:id" element={<PaginaDetalheSolicitacao />} />
            <Route path="/solicitacoes/:id/marcacao" element={<PaginaMarcacao />} />
          </Route>

          {/* Demanda reprimida: o estoque anterior ao KAVI, em menu próprio.
              Todos acompanham; registrar é da Regulação e da gestão, que são
              quem tem o papel em mãos. O backend repete as duas regras. */}
          <Route
            element={<RotaProtegida perfis={['UBS', 'REGULACAO', 'GESTOR', 'ADMINISTRADOR']} />}
          >
            <Route
              path="/demanda-reprimida"
              element={<PaginaListaSolicitacoes origemFixa="DEMANDA_REPRIMIDA" />}
            />
          </Route>

          <Route element={<RotaProtegida perfis={['REGULACAO', 'GESTOR']} />}>
            <Route
              path="/demanda-reprimida/nova"
              element={<PaginaNovaSolicitacao origem="DEMANDA_REPRIMIDA" />}
            />
          </Route>

          {/* Histórico do paciente no eSUS e no SISREG: todo perfil consulta,
              e toda consulta vai para a auditoria. */}
          <Route
            element={<RotaProtegida perfis={['UBS', 'REGULACAO', 'GESTOR', 'ADMINISTRADOR']} />}
          >
            <Route path="/historico-paciente" element={<PaginaHistoricoPaciente />} />
          </Route>

          {/* Relatórios são de gestão: a Regulação opera a fila, quem lê o
              panorama é quem gere. O escopo por UBS continua valendo dentro
              de cada consulta. */}
          <Route element={<RotaProtegida perfis={['GESTOR', 'ADMINISTRADOR']} />}>
            <Route path="/relatorios" element={<PaginaRelatorios />} />
          </Route>

          <Route element={<RotaProtegida perfis={['UBS', 'GESTOR']} />}>
            <Route path="/solicitacoes/nova" element={<PaginaNovaSolicitacao />} />
          </Route>

          {/* Correção de dados: a UBS fala com o paciente, a Regulação vê a
              divergência com a requisição do médico, e a gestão corrige o que
              a unidade já não tem condição de corrigir. O backend restringe o
              escopo por UBS e recusa solicitação já agendada. */}
          <Route
            element={<RotaProtegida perfis={['UBS', 'REGULACAO', 'GESTOR', 'ADMINISTRADOR']} />}
          >
            <Route path="/solicitacoes/:id/editar" element={<PaginaEditarSolicitacao />} />
          </Route>

          <Route element={<RotaProtegida perfis={['ADMINISTRADOR']} />}>
            <Route path="/admin/usuarios" element={<PaginaUsuarios />} />
            <Route path="/admin/especialidades" element={<PaginaEspecialidades />} />
            <Route path="/admin/procedimentos" element={<PaginaProcedimentos />} />
            <Route path="/admin/locais" element={<PaginaLocais />} />
            <Route path="/admin/profissionais" element={<PaginaProfissionais />} />
            <Route path="/admin/auditoria" element={<PaginaAuditoria />} />
            <Route path="/admin/pec" element={<PaginaPec />} />
            <Route path="/admin/esus" element={<PaginaEsus />} />
            <Route path="/admin/sisreg" element={<PaginaSisreg />} />
            <Route path="/admin/n8n" element={<PaginaN8n />} />
            <Route path="/admin/feriados" element={<PaginaFeriados />} />
            <Route path="/admin/smtp" element={<PaginaSmtp />} />
            <Route path="/admin/modelos-email" element={<PaginaModelosEmail />} />
            <Route path="/admin/retencao" element={<PaginaRetencao />} />
            <Route path="/admin/zona-de-perigo" element={<PaginaZonaDePerigo />} />
          </Route>

          <Route element={<RotaProtegida perfis={['ADMINISTRADOR', 'REGULACAO']} />}>
            <Route path="/admin/ubs" element={<PaginaUbs />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<PaginaNaoEncontrada />} />
    </Routes>
  );
}
